import { useState, useEffect, FormEvent } from 'react';
import { User as UserIcon, Mail, Scale, Ruler, Calendar, Repeat, Circle, Moon, Sun, Coins } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { userApi, aiApi } from '@/services/api';
import type { CreditLogEntry } from '@/services/api';
import { Card, Button } from '@/components/common';
import type { AiUsageStats } from '@/types';
import { calculateAgeFromBirthDate, toDateInputValue } from '@/lib/age';

const Profile = () => {
  const { user, setUser } = useAuthStore();
  const { showToast, darkMode, setDarkMode } = useUIStore();
  
  const [name, setName] = useState(user?.name || '');
  const [birthDate, setBirthDate] = useState(toDateInputValue(user?.birthDate));
  const [weight, setWeight] = useState(user?.weight?.toString() || '');
  const [height, setHeight] = useState(user?.height?.toString() || '');
  const [defaultReps, setDefaultReps] = useState(() => localStorage.getItem('defaultReps') || '12');
  const [loading, setLoading] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [aiUsage, setAiUsage] = useState<AiUsageStats | null>(null);
  const [creditLogs, setCreditLogs] = useState<CreditLogEntry[]>([]);

  // Format last active time
  const formatLastActive = (lastActiveAt: string | null) => {
    if (!lastActiveAt) return 'Nincs adat';
    
    const lastActive = new Date(lastActiveAt);
    const now = new Date();
    const diffMs = now.getTime() - lastActive.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // If active within last 2 minutes, consider "active"
    if (diffMins < 2) return 'Aktív';
    if (diffMins < 60) return `${diffMins} perce`;
    if (diffHours < 24) return `${diffHours} órája`;
    if (diffDays < 7) return `${diffDays} napja`;
    
    return lastActive.toLocaleDateString('hu-HU');
  };

  // Update activity status periodically
  useEffect(() => {
    const checkActivity = () => {
      if (user?.lastActiveAt) {
        const lastActive = new Date(user.lastActiveAt);
        const now = new Date();
        const diffMs = now.getTime() - lastActive.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        setIsActive(diffMins < 2);
      }
    };

    checkActivity();
    const interval = setInterval(checkActivity, 30000);
    return () => clearInterval(interval);
  }, [user?.lastActiveAt]);

  useEffect(() => {
    setBirthDate(toDateInputValue(user?.birthDate));
  }, [user?.birthDate]);

  useEffect(() => {
    const loadAiData = async () => {
      try {
        const [usageRes, historyRes] = await Promise.all([
          aiApi.getMyUsage(),
          aiApi.getMyCreditHistory(),
        ]);
        if (usageRes.success && usageRes.data) {
          setAiUsage(usageRes.data);
        }
        if (historyRes.success && historyRes.data) {
          setCreditLogs(historyRes.data.logs);
        }
      } catch {
        // AI usage not critical
      }
    };
    loadAiData();
  }, []);

  // Handle dark mode toggle
  const handleDarkModeToggle = async () => {
    if (!user) return;
    
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    
    try {
      const response = await userApi.update(user.id, { darkMode: newDarkMode });
      if (response.success && response.data) {
        setUser(response.data.user);
        showToast(newDarkMode ? 'Sötét mód bekapcsolva' : 'Világos mód bekapcsolva', 'success');
      }
    } catch (error) {
      console.error('Failed to save dark mode preference:', error);
      // Revert on error
      setDarkMode(!newDarkMode);
      showToast('Hiba történt a mentés során.', 'error');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);

    try {
      const response = await userApi.update(user.id, {
        name,
        birthDate: birthDate || null,
        weight: weight ? parseFloat(weight) : null,
        height: height ? parseFloat(height) : null,
      });

      if (response.success && response.data) {
        setUser(response.data.user);
        // Save default reps to localStorage
        if (defaultReps) {
          localStorage.setItem('defaultReps', defaultReps);
        }
        showToast('Profil sikeresen mentve!', 'success');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      showToast('Hiba történt a mentés során.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Profil beállítások</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Személyes adataid kezelése</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Személyes adatok + Kredit kártyák */}
        <div className="lg:col-span-2 space-y-6">
          <Card title="Személyes adatok">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                    <UserIcon className="w-4 h-4 inline mr-1" />
                    Név
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email
                  </label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-gray-300 rounded-lg bg-gray-50"
                    value={user?.email || ''}
                    disabled
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Az email cím nem módosítható.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Születési dátum
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                  />
                  {birthDate && calculateAgeFromBirthDate(birthDate) !== null && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {calculateAgeFromBirthDate(birthDate)} éves
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                    <Scale className="w-4 h-4 inline mr-1" />
                    Súly (kg)
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="pl. 85"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    min="20"
                    max="500"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                    <Ruler className="w-4 h-4 inline mr-1" />
                    Magasság (cm)
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="pl. 178"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    min="50"
                    max="300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                    <Repeat className="w-4 h-4 inline mr-1" />
                    Alapértelmezett ismétlésszám
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="pl. 12"
                    value={defaultReps}
                    onChange={(e) => setDefaultReps(e.target.value)}
                    min="1"
                    max="100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Ez az érték jelenik meg alapértelmezettként sorozat rögzítéskor.</p>
                </div>
              </div>

              <div className="mt-6">
                <Button type="submit" loading={loading}>
                  Mentés
                </Button>
              </div>
            </form>
          </Card>

          {/* Kredit egyenleg + Kredit előzmények egymás mellett */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card title="Kredit egyenleg">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Coins className="w-5 h-5 text-amber-500" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Elérhető kredit</span>
                  </div>
                </div>
                <div className="text-center py-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="text-3xl font-bold text-amber-700 dark:text-amber-400">
                    {user?.credits !== undefined ? user.credits.toFixed(2) : '—'}
                  </div>
                  <div className="text-xs text-amber-600 dark:text-amber-500 mt-1">kredit</div>
                </div>
                {aiUsage && aiUsage.total.calls > 0 && (
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                    <div className="text-center">
                      <div className="text-sm font-bold text-gray-900 dark:text-gray-100">{aiUsage.total.calls}</div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">Hívás</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-gray-900 dark:text-gray-100">
                        {aiUsage.total.totalTokens >= 1000 ? `${(aiUsage.total.totalTokens / 1000).toFixed(1)}K` : aiUsage.total.totalTokens}
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">Token</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-red-600 dark:text-red-400">{(aiUsage.total.costUsd * 1000).toFixed(2)}</div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">Felhasznált</div>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Card title="Kredit előzmények">
              {creditLogs.length > 0 ? (
                <div className="space-y-0 -mx-4 -mb-4 max-h-64 overflow-y-auto">
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {creditLogs.map((log) => {
                      const creditUsed = Math.round(log.costUsd * 1000 * 100) / 100;
                      const sourceLabels: Record<string, string> = {
                        'workout-builder': 'Edzés összeállító',
                        'training-plan-builder': 'Edzésterv',
                        'admin_test': 'Admin teszt',
                        'description': 'Leírás generálás',
                      };
                      const sourceLabel = sourceLabels[log.source] || log.source;
                      return (
                        <div key={log.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {sourceLabel}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                              <span>
                                {new Date(log.createdAt).toLocaleDateString('hu-HU', { month: '2-digit', day: '2-digit' })}{' '}
                                {new Date(log.createdAt).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              <span className="text-gray-300 dark:text-gray-600">|</span>
                              <span>{log.model}</span>
                              <span className="text-gray-300 dark:text-gray-600">|</span>
                              <span>{log.totalTokens.toLocaleString('hu')} token</span>
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-red-600 dark:text-red-400 whitespace-nowrap ml-3">
                            -{creditUsed.toFixed(2)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400 dark:text-gray-500">
                  <Coins className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Még nincs kredit tranzakció</p>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Right: Fiók info + Megjelenés */}
        <div>
          <Card title="Fiók információ">
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-600 dark:text-gray-300">Állapot:</dt>
                <dd className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                  <span className="inline-flex items-center gap-1.5">
                    <Circle 
                      className={`w-2.5 h-2.5 ${isActive ? 'text-green-500 fill-green-500' : 'text-gray-400 fill-gray-400'}`} 
                    />
                    {isActive ? (
                      <span className="text-green-600 dark:text-green-400 font-medium">Aktív</span>
                    ) : (
                      <span className="text-gray-600 dark:text-gray-400">{formatLastActive(user?.lastActiveAt || null)}</span>
                    )}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-600 dark:text-gray-300">Regisztráció:</dt>
                <dd className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                  {user?.createdAt
                    ? new Date(user.createdAt).toLocaleDateString('hu-HU', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : '-'}
                </dd>
              </div>
            </dl>
          </Card>

          <Card title="Megjelenés" className="mt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {darkMode ? (
                  <Moon className="w-5 h-5 text-indigo-500" />
                ) : (
                  <Sun className="w-5 h-5 text-yellow-500" />
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Sötét mód</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {darkMode ? 'Bekapcsolva' : 'Kikapcsolva'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleDarkModeToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  darkMode ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    darkMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
